import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";
import { Search, Trash, RefreshCw, AlertCircle, Loader2, Download, ChevronDown, ChevronRight, X, ChevronLeft, Copy, ExternalLink, Eye, ArrowRight } from "lucide-react";
import { useApi } from "../../lib/ApiContext";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { toast, Toaster } from 'sonner';
import { Progress } from "../ui/progress";
import { getColumns } from './columns';
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { exportTableToCSV } from "../../lib/csvExport";
import { DownloadProgressDialog } from "../ui/download-progress";
import { Checkbox } from "../ui/checkbox";
import {
    Sheet,
    SheetContent,
    SheetContentFull,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from "../ui/sheet";

// Import the date formatting utility from columns.jsx
import { formatDateToLocalTimezone } from './columns';

const PagesIndex = () => {
    const { orgPublicId, templatePublicId } = useParams();
    const navigate = useNavigate();
    const api = useApi();

    // Preview feature states
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewVersion, setPreviewVersion] = useState(null);
    const [previewPage, setPreviewPage] = useState(null);
    const [previewPrompt, setPreviewPrompt] = useState('');
    const [pageVersionsList, setPageVersionsList] = useState([]);
    const [currentVersionIndex, setCurrentVersionIndex] = useState(0);

    // Function to open preview sheet
    const handleOpenPreview = (version, page) => {
        // Get all versions for this page
        const allVersions = pageVersions[page.id] || [];

        // Find the index of the selected version
        const versionIndex = allVersions.findIndex(v => v.id === version.id);

        setPreviewVersion(version);
        setPreviewPage(page);
        setPreviewPrompt('');
        setPageVersionsList(allVersions);
        setCurrentVersionIndex(versionIndex >= 0 ? versionIndex : 0);
        setIsPreviewOpen(true);
    };

    // Function to handle cycling to the next version
    const handlePreviewNextVersion = () => {
        if (pageVersionsList.length <= 1) return;

        // Calculate the next index, wrapping around to 0 if at the end
        const nextIndex = (currentVersionIndex + 1) % pageVersionsList.length;

        // Update the current version
        setCurrentVersionIndex(nextIndex);
        setPreviewVersion(pageVersionsList[nextIndex]);
        setPreviewPrompt(''); // Reset the prompt for the new version
    };

    // Function to handle submitting the preview edit
    const handleSubmitPreviewEdit = async () => {
        if (!previewPrompt.trim()) {
            toast.error("Please enter edit instructions");
            return;
        }

        try {
            // Show loading state
            setIsSubmittingEdit(true);

            // Show loading toast
            toast.loading("Submitting page regeneration request...");

            // Construct the URL for the template and page
            const url = `${organization.custom_domain}${template.template_path}`;

            // Call the service binding API
            const response = await api.serviceBindings.regeneratePageWithLLM({
                url,
                keyword: previewPage.keyword_phrase,
                instructions: previewPrompt,
                versionId: previewVersion.id // Pass the current version ID as reference
            });

            if (response && response.success) {
                toast.dismiss();
                toast.success("Page regeneration initiated", {
                    description: response.message || "A new version is being generated based on your instructions."
                });

                // Close the preview dialog
                setIsPreviewOpen(false);

                // Reset the preview prompt
                setPreviewPrompt('');

                // Refresh the pages list after a delay to allow for processing
                setTimeout(async () => {
                    try {
                        await fetchAllPages(template.id, searchQuery, false);
                    } catch (err) {
                        console.error("Failed to refresh pages:", err);
                    }
                }, 5000);
            } else {
                toast.dismiss();
                toast.error("Failed to regenerate page", {
                    description: response?.error || "Unknown error occurred"
                });
            }
        } catch (error) {
            console.error('Error regenerating page:', error);
            toast.dismiss();
            toast.error("Error regenerating page", {
                description: error.message
            });
        } finally {
            setIsSubmittingEdit(false);
        }
    };

    // VersionsTable component to display versions
    const VersionsTable = ({ versions, page, template, organization, onUpdateStatus, onDeleteVersion }) => {
        if (!versions || versions.length === 0) {
            return (
                <div className="p-4 text-center text-gray-500">
                    No versions found for this page.
                </div>
            );
        }

        return (
            <div className="p-4 bg-slate-50 border-t rounded-b-md">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="text-xs bg-slate-100">
                                <th className="p-2 text-left font-medium">Version</th>
                                <th className="p-2 text-center font-medium">URL</th>
                                <th className="p-2 text-center font-medium">Status</th>
                                <th className="p-2 text-center font-medium">Total Views</th>
                                <th className="p-2 text-center font-medium">Last Viewed On</th>
                                <th className="p-2 text-center font-medium">Created On</th>
                                <th className="p-2 text-center font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {versions.map(version => (
                                <tr key={version.id} className="border-b border-slate-100 text-sm">
                                    <td className="p-2">v{version.versionNumber}</td>
                                    <td className="p-2 text-center">
                                        <div className="flex space-x-2 justify-center">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 w-7 p-0"
                                                title="Copy URL"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(constructPageUrl(template, page, version));
                                                    toast.success("URL copied to clipboard");
                                                }}
                                            >
                                                <Copy className="h-3 w-3" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 w-7 p-0"
                                                title="Visit URL"
                                                onClick={() => window.open(constructPageUrl(template, page, version), '_blank')}
                                            >
                                                <ExternalLink className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </td>
                                    <td className="p-2 text-center">
                                        {version.status === 'loading' ? (
                                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                Loading...
                                            </span>
                                        ) : (
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span
                                                            className={`px-2 py-1 rounded-full text-xs font-medium ${version.status === 'unknown'
                                                                ? "bg-gray-100 text-gray-800"
                                                                : version.status === "live"
                                                                    ? "bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer"
                                                                    : "bg-gray-100 text-gray-800 hover:bg-gray-200 cursor-pointer"
                                                                }`}
                                                            style={{
                                                                cursor: version.status !== 'unknown' ? 'pointer' : 'default'
                                                            }}
                                                            onClick={(e) => {
                                                                if (version.status === 'unknown') return;
                                                                e.stopPropagation();
                                                                const newStatus = version.status === 'live' ? 'archived' : 'live';
                                                                onUpdateStatus(version.cache_key, newStatus);
                                                            }}
                                                        >
                                                            {version.status === 'unknown' ? 'Unknown' : version.status}
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {version.status === 'unknown'
                                                            ? 'Unknown page status'
                                                            : version.status === 'live'
                                                                ? 'Click to archive'
                                                                : 'Click to make live'}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )}
                                    </td>
                                    <td className="p-2 text-center">{version.total_views}</td>
                                    <td className="p-2 text-center">
                                        {version.last_viewed_on
                                            ? formatDateToLocalTimezone(version.last_viewed_on)
                                            : "Never"}
                                    </td>
                                    <td className="p-2 text-center">
                                        {version.created_on
                                            ? formatDateToLocalTimezone(version.created_on)
                                            : "Unknown"}
                                    </td>
                                    <td className="p-2 text-center">
                                        <div className="flex space-x-2 justify-center">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 px-2"
                                                title="Preview and edit"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenPreview(version, page);
                                                }}
                                            >
                                                <Eye className="h-3 w-3 mr-1" />
                                            </Button>
                                            {version.status === 'archived' && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    title="Delete version"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDeleteVersion(version, page);
                                                    }}
                                                >
                                                    <Trash className="h-3 w-3" />
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // Add sort state
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });

    // Function to handle sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });

        // Reset to first page when sort changes
        setCurrentPage(0);
    };

    // Sort data based on current sort configuration
    const getSortedData = (data) => {
        if (!sortConfig.key) return data;

        return [...data].sort((a, b) => {
            // Get values based on the sort key
            let aValue, bValue;

            if (sortConfig.key === 'keyword_phrase') {
                aValue = a.keyword_phrase?.toLowerCase() || '';
                bValue = b.keyword_phrase?.toLowerCase() || '';
            }
            else if (sortConfig.key === 'total_views') {
                // For total views, sum from all versions
                aValue = (pageVersions[a.id] || []).reduce((sum, version) => sum + (version.total_views || 0), 0);
                bValue = (pageVersions[b.id] || []).reduce((sum, version) => sum + (version.total_views || 0), 0);
            }
            else if (sortConfig.key === 'last_viewed_on') {
                // For last viewed, get the most recent date
                aValue = null;
                bValue = null;

                (pageVersions[a.id] || []).forEach(version => {
                    if (version.last_viewed_on) {
                        const viewDate = new Date(version.last_viewed_on);
                        if (!aValue || viewDate > aValue) {
                            aValue = viewDate;
                        }
                    }
                });

                (pageVersions[b.id] || []).forEach(version => {
                    if (version.last_viewed_on) {
                        const viewDate = new Date(version.last_viewed_on);
                        if (!bValue || viewDate > bValue) {
                            bValue = viewDate;
                        }
                    }
                });

                // If either value is null, handle appropriately
                if (!aValue && !bValue) return 0;
                if (!aValue) return 1;
                if (!bValue) return -1;
            }
            else if (sortConfig.key === 'created_on') {
                // For created on, get the oldest date
                aValue = null;
                bValue = null;

                (pageVersions[a.id] || []).forEach(version => {
                    if (version.created_on) {
                        const creationDate = new Date(version.created_on);
                        if (!aValue || creationDate < aValue) {
                            aValue = creationDate;
                        }
                    }
                });

                (pageVersions[b.id] || []).forEach(version => {
                    if (version.created_on) {
                        const creationDate = new Date(version.created_on);
                        if (!bValue || creationDate < bValue) {
                            bValue = creationDate;
                        }
                    }
                });

                // If either value is null, handle appropriately
                if (!aValue && !bValue) return 0;
                if (!aValue) return 1;
                if (!bValue) return -1;
            }
            else if (sortConfig.key === 'live_versions') {
                // For live versions, count the number of live versions
                aValue = (pageVersions[a.id] || []).filter(version => version.status === 'live').length;
                bValue = (pageVersions[b.id] || []).filter(version => version.status === 'live').length;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    // CollapsibleDataTable component to enhance the existing DataTable with expandable rows
    const CollapsibleDataTable = ({
        data,
        columns,
        onSearch,
        searchQuery,
        selectedRows,
        onSelectedRowsChange,
        onBulkAction,
        isLoadingMetadata,
        onExportCSV,
        expandedRows,
        onRowClick,
        pageVersions,
        organization,
        template,
        page,
        pageCount,
        onPageChange,
        pageSize,
        onPageSizeChange
    }) => {
        // Local state for search input value
        const [searchInputValue, setSearchInputValue] = useState(searchQuery);

        // Get sorted data
        const sortedData = getSortedData(data);

        // Get current page of data
        const startIndex = page * pageSize;
        const endIndex = Math.min(startIndex + pageSize, sortedData.length);
        const currentPageData = sortedData.slice(startIndex, endIndex);

        // Row click handler that prevents page reset
        const handleRowClick = (pageId, event) => {
            // Stop event propagation to prevent it from affecting parent elements
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }

            // Call the provided click handler
            onRowClick(pageId);
        };

        // Create a modified row renderer that includes the collapsible functionality
        const renderRows = () => {
            if (!data || data.length === 0) {
                return (
                    <tr>
                        <td colSpan={columns.length + 1} className="h-24 text-center">
                            {searchQuery ? "No pages matching your search" : "No pages found for this template"}
                        </td>
                    </tr>
                );
            }

            // Use sorted and paginated data
            const rows = [];

            currentPageData.forEach(page => {
                // Main row
                rows.push(
                    <tr
                        key={page.id}
                        className={`cursor-pointer hover:bg-slate-50 border-b ${expandedRows[page.id] ? 'bg-slate-100' : ''}`}
                        onClick={(e) => handleRowClick(page.id, e)}
                    >
                        {columns.map(column => (
                            <td key={column.id} className="p-2">
                                {column.cell ? column.cell(page) : page[column.accessorKey]}
                            </td>
                        ))}
                        <td className="p-2 text-center">
                            {pageVersions[page.id]?.length > 0 ? (
                                expandedRows[page.id] ? (
                                    <ChevronDown
                                        className="h-4 w-4 inline-block text-gray-500"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRowClick(page.id, e);
                                        }}
                                    />
                                ) : (
                                    <ChevronRight
                                        className="h-4 w-4 inline-block text-gray-500"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRowClick(page.id, e);
                                        }}
                                    />
                                )
                            ) : (
                                <span className="text-xs text-gray-400">No versions</span>
                            )}
                        </td>
                    </tr>
                );

                // Expanded content row
                if (expandedRows[page.id] && pageVersions[page.id]?.length > 0) {
                    rows.push(
                        <tr key={`${page.id}-expanded`}>
                            <td colSpan={columns.length + 1} className="p-0 border-b">
                                <div className="bg-slate-50 rounded-b-md overflow-hidden">
                                    <VersionsTable
                                        versions={pageVersions[page.id] || []}
                                        page={page}
                                        template={template}
                                        organization={organization}
                                        onUpdateStatus={updateVersionStatus}
                                        onDeleteVersion={handleDeleteVersion}
                                    />
                                </div>
                            </td>
                        </tr>
                    );
                }
            });

            return rows;
        };

        // Function to render sortable column header
        const renderSortableHeader = (column) => {
            // Handle checkbox column differently
            if (column.id === 'select') {
                // Compute the current page data
                const sortedData = getSortedData(data);
                const startIndex = page * pageSize;
                const endIndex = Math.min(startIndex + pageSize, sortedData.length);
                const currentPageData = sortedData.slice(startIndex, endIndex);

                return (
                    <div className="flex justify-center">
                        <Checkbox
                            className="h-4 w-4"
                            checked={
                                currentPageData.length > 0 &&
                                currentPageData.every(row => selectedRows.some(selected => selected.id === row.id))
                            }
                            onCheckedChange={(checked) => {
                                if (checked) {
                                    // Add only current page rows that aren't already selected
                                    const currentPageIds = new Set(currentPageData.map(row => row.id));
                                    const newSelectedRows = [
                                        ...selectedRows.filter(row => !currentPageIds.has(row.id)), // Keep selected rows from other pages
                                        ...currentPageData // Add all rows from current page
                                    ];
                                    handleSelectedRowsChange(newSelectedRows);
                                } else {
                                    // Remove only current page rows from selection
                                    const currentPageIds = new Set(currentPageData.map(row => row.id));
                                    handleSelectedRowsChange(
                                        selectedRows.filter(row => !currentPageIds.has(row.id))
                                    );
                                }
                            }}
                            aria-label="Select all visible rows"
                        />
                    </div>
                );
            }

            // Skip rendering sort UI for URL column
            if (column.id === 'url') {
                return column.header;
            }

            const isSorted = sortConfig.key === column.id;
            const direction = isSorted ? sortConfig.direction : null;

            // Add text-center class for specific columns
            const centerAlignedColumns = ['total_views', 'last_viewed_on', 'created_on', 'live_versions'];
            const alignClass = centerAlignedColumns.includes(column.id) ? 'justify-center' : '';

            return (
                <div
                    className={`flex items-center cursor-pointer select-none ${alignClass}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSort(column.id);
                    }}
                >
                    <span>{column.header}</span>
                    <span className="ml-1">
                        {isSorted && direction === 'asc' && '▲'}
                        {isSorted && direction === 'desc' && '▼'}
                    </span>
                </div>
            );
        };

        // Pagination UI component
        const Pagination = () => {
            return (
                <div className="flex items-center justify-between px-2 py-3 border-t">
                    <div className="flex-1 flex justify-between sm:hidden">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onPageChange(Math.max(0, page - 1))}
                            disabled={page === 0}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onPageChange(Math.min(pageCount - 1, page + 1))}
                            disabled={page >= pageCount - 1}
                        >
                            Next
                        </Button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-700">Show</span>
                            <select
                                className="h-8 rounded-md border border-input px-3 py-1 text-sm"
                                value={pageSize}
                                onChange={(e) => {
                                    const newPageSize = Number(e.target.value);
                                    onPageSizeChange(newPageSize);
                                }}
                            >
                                {[10, 25, 50, 100, 250, 500].map(size => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                            <span className="text-sm text-gray-700">entries</span>
                        </div>

                        <div>
                            <p className="text-sm text-gray-700">
                                Showing <span className="font-medium">{sortedData.length > 0 ? page * pageSize + 1 : 0}</span> to{' '}
                                <span className="font-medium">
                                    {Math.min((page + 1) * pageSize, sortedData.length)}
                                </span>{' '}
                                of <span className="font-medium">{sortedData.length}</span> results
                            </p>
                        </div>
                        <div>
                            <div className="flex items-center space-x-2">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => onPageChange(0)}
                                    disabled={page === 0}
                                    title="Go to first page"
                                    className="h-8 w-8"
                                >
                                    <span className="sr-only">Go to first page</span>
                                    <ChevronLeft className="h-4 w-4" />
                                    <ChevronLeft className="h-4 w-4 -ml-2" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => onPageChange(Math.max(0, page - 1))}
                                    disabled={page === 0}
                                    title="Go to previous page"
                                    className="h-8 w-8"
                                >
                                    <span className="sr-only">Go to previous page</span>
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                                    Page {page + 1} of {pageCount || 1}
                                </div>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => onPageChange(Math.min(pageCount - 1, page + 1))}
                                    disabled={page >= pageCount - 1}
                                    title="Go to next page"
                                    className="h-8 w-8"
                                >
                                    <span className="sr-only">Go to next page</span>
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => onPageChange(pageCount - 1)}
                                    disabled={page >= pageCount - 1}
                                    title="Go to last page"
                                    className="h-8 w-8"
                                >
                                    <span className="sr-only">Go to last page</span>
                                    <ChevronRight className="h-4 w-4" />
                                    <ChevronRight className="h-4 w-4 -ml-2" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        };

        // Synchronize local state with prop changes
        useEffect(() => {
            setSearchInputValue(searchQuery);
        }, [searchQuery]);

        // Handle search input change
        const handleSearchInputChange = (e) => {
            setSearchInputValue(e.target.value);
        };

        // Handle key press in search input
        const handleSearchKeyPress = (e) => {
            // Only trigger search when Enter key is pressed
            if (e.key === 'Enter') {
                onSearch(searchInputValue);
            }
        };

        return (
            <div>
                <div className="flex items-center justify-between mb-4">
                    <div className="relative max-w-sm flex items-center">
                        <div className="relative flex-grow w-64">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search pages..."
                                className="pl-8 pr-8 w-full"
                                value={searchInputValue}
                                onChange={handleSearchInputChange}
                                onKeyDown={handleSearchKeyPress}
                            />
                            {searchInputValue && (
                                <button
                                    type="button"
                                    className="absolute right-2 top-2.5 text-muted-foreground hover:text-gray-700"
                                    onClick={() => {
                                        setSearchInputValue('');
                                        onSearch('');
                                    }}
                                    aria-label="Clear search"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="ml-2"
                            onClick={() => onSearch(searchInputValue)}
                        >
                            Search
                        </Button>
                    </div>

                    <div className="flex items-center space-x-2">
                        {isLoadingMetadata && (
                            <p className="text-sm text-amber-600 mr-2">
                                Loading metadata...
                            </p>
                        )}

                        {selectedRows.length > 0 && (
                            <>
                                <p className="text-sm font-medium mr-2">
                                    {selectedRows.length} selected
                                </p>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => onBulkAction?.('regenerate')}
                                >
                                    <RefreshCw className="h-4 w-4 mr-1" />
                                    Regenerate
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => onBulkAction?.('delete')}
                                >
                                    <Trash className="h-4 w-4 mr-1" />
                                    Delete Phrase(s)
                                </Button>
                            </>
                        )}

                        {onExportCSV && (
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={onExportCSV}
                                className="h-9 w-9 ml-2"
                                title="Export CSV"
                            >
                                <Download className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>

                <div className="rounded-md border">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-slate-50 border-b">
                                {columns.map(column => (
                                    <th key={column.id} className={`p-2 ${column.id === 'select' ? 'w-16' : ''} ${column.id === 'select' || column.id === 'url' || column.id === 'live_versions' ||
                                        column.id === 'total_views' || column.id === 'last_viewed_on' || column.id === 'created_on'
                                        ? 'text-center'
                                        : 'text-left'
                                        } font-medium`}>
                                        {renderSortableHeader(column)}
                                    </th>
                                ))}
                                <th className="p-2 text-center font-medium w-10">
                                    <span className="sr-only">Expand</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderRows()}
                        </tbody>
                    </table>
                    <Pagination />
                </div>
            </div>
        );
    };

    const [template, setTemplate] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [pages, setPages] = useState([]);
    const [filteredPages, setFilteredPages] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);

    // Cursor-based pagination state (API pagination)
    const [isLoadingMorePages, setIsLoadingMorePages] = useState(false);
    const [fetchProgress, setFetchProgress] = useState(0);
    const [totalFetched, setTotalFetched] = useState(0);
    const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);

    // Dialog state
    const [pageToDelete, setPageToDelete] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);

    // Add pages dialog state
    const [showAddPagesDialog, setShowAddPagesDialog] = useState(false);
    const [keywordsInput, setKeywordsInput] = useState('');
    const [isAddingPages, setIsAddingPages] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [shouldReprocess, setShouldReprocess] = useState(false);

    // State for bulk actions
    const [showBulkActionDialog, setShowBulkActionDialog] = useState(false);
    const [bulkAction, setBulkAction] = useState(null);
    const [isBulkActionProcessing, setIsBulkActionProcessing] = useState(false);
    const [deletionJobId, setDeletionJobId] = useState(null);
    const [deletionJobStatus, setDeletionJobStatus] = useState(null);
    const [isDeletionJobPolling, setIsDeletionJobPolling] = useState(false);

    // Add regeneration job polling state
    const [regenerationJobId, setRegenerationJobId] = useState(null);
    const [regenerationJobStatus, setRegenerationJobStatus] = useState(null);
    const [isRegenerationJobPolling, setIsRegenerationJobPolling] = useState(false);

    // Add CSV download state
    const [isGeneratingCSV, setIsGeneratingCSV] = useState(false);
    const [csvProgress, setCsvProgress] = useState(0);

    // Add versions state and expanded rows state
    const [pageVersions, setPageVersions] = useState({});
    const [expandedRows, setExpandedRows] = useState({});

    // In the component state section, add pagination state variables
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [pageCount, setPageCount] = useState(0);

    // In the component state section, add isSubmittingEdit state
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

    // Add a handler function for page size changes
    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(0); // Reset to first page when changing page size
    };

    // Function to toggle row expansion
    const toggleRowExpansion = (pageId) => {
        // Using functional update to avoid stale state
        setExpandedRows(prev => {
            const newState = { ...prev };

            // If row is already expanded, collapse it
            if (newState[pageId]) {
                delete newState[pageId];
            } else {
                // Otherwise expand it
                newState[pageId] = true;
            }

            return newState;
        });

        // Prevent the toggle from affecting pagination
        // by stopping propagation or default behavior in the component
    };

    // Function to update a version's status
    const updateVersionStatus = async (versionKey, newStatus) => {
        try {
            // Show loading toast
            toast.loading(`Updating version status to ${newStatus}...`);

            // Call the API to update the status
            const response = await api.pages.updateVersionStatus({
                version_key: versionKey,
                status: newStatus
            });

            if (response && response.success) {
                toast.dismiss();
                toast.success(`Status updated to ${newStatus}`);

                // Update our local state
                setPageVersions(prev => {
                    const updated = { ...prev };

                    // Find the page and version
                    Object.keys(updated).forEach(pageId => {
                        const versionIndex = updated[pageId].findIndex(v => v.cache_key === versionKey);
                        if (versionIndex !== -1) {
                            updated[pageId][versionIndex] = {
                                ...updated[pageId][versionIndex],
                                status: newStatus,
                                status_updated_at: response.updated_at
                            };
                        }
                    });

                    return updated;
                });

                // Also update the preview version if it matches the one being updated
                if (previewVersion && previewVersion.cache_key === versionKey) {
                    setPreviewVersion(prevVersion => ({
                        ...prevVersion,
                        status: newStatus,
                        status_updated_at: response.updated_at
                    }));

                    // Update in the pageVersionsList as well to keep everything in sync
                    setPageVersionsList(prevList => {
                        return prevList.map(version => {
                            if (version.cache_key === versionKey) {
                                return {
                                    ...version,
                                    status: newStatus,
                                    status_updated_at: response.updated_at
                                };
                            }
                            return version;
                        });
                    });
                }
            } else {
                toast.dismiss();
                toast.error("Failed to update status", {
                    description: response?.error || "Unknown error occurred"
                });
            }
        } catch (error) {
            console.error('Error updating version status:', error);
            toast.dismiss();
            toast.error("Error updating status", {
                description: error.message
            });
        }
    };

    // Generate versions for all pages initially for the Live column
    useEffect(() => {
        if (filteredPages.length > 0 && Object.keys(pageVersions).length === 0) {
            // Initialize empty versions for each page
            const initialVersions = {};
            filteredPages.forEach(page => {
                initialVersions[page.id] = [];
            });
            setPageVersions(initialVersions);
        }
    }, [filteredPages]);

    // Function to construct the URL
    const constructPageUrl = (template, page, version) => {
        if (!template?.template_path || !page?.keyword_phrase || !organization?.custom_domain) return '#';

        // Per database requirements:
        // - custom_domain always ends with a slash
        // - template_path never starts with a slash but always ends with a slash
        const baseUrl = `${organization.custom_domain}${template.template_path}`;

        // Format the keyword phrase (replace spaces with +)
        const encodedKeyword = page.keyword_phrase.replace(/\s+/g, '+');

        // Construct the final URL
        // Check if the base URL already has query parameters
        const separator = baseUrl.includes('?') ? '&' : '?';

        // Add version parameter if specified
        let url = `${baseUrl}${separator}utm_term=${encodedKeyword}`;

        // If we have a version and it's not the default/latest, add version parameter
        if (version && version.versionNumber) {
            url += `&ep_ver=${version.versionNumber}`;
        }

        // Add preview parameter
        url += `&ep_preview=true`;

        return url;
    };

    // Function to construct underscore-separated URL for CSV export
    const constructUnderscoreUrl = (template, page, version) => {
        if (!template?.template_path || !page?.keyword_phrase || !organization?.custom_domain) return '#';

        // Per database requirements:
        // - custom_domain always ends with a slash
        // - template_path never starts with a slash but always ends with a slash

        // Handle query parameters in template_path
        let baseUrl = organization.custom_domain;
        let queryParams = '';

        // Extract the base path and any query parameters
        const templatePath = template.template_path;
        const [basePath, queryString] = templatePath.split('?');

        // Remove trailing slash for formatting
        const templatePathWithoutTrailingSlash = basePath.endsWith('/')
            ? basePath.slice(0, -1)
            : basePath;

        // Format the keyword phrase (replace spaces with underscores)
        const underscoreKeyword = page.keyword_phrase.replace(/\s+/g, '_').toLowerCase();

        // Construct the base URL with the underscored keyword
        const url = `${baseUrl}${templatePathWithoutTrailingSlash}/${underscoreKeyword}`;

        // Add query parameters if they exist
        if (queryString) {
            queryParams = `?${queryString}`;
        }

        // Add version parameter if specified
        if (version && version.versionNumber) {
            queryParams = queryParams
                ? `${queryParams}&ep_ver=${version.versionNumber}`
                : `?ep_ver=${version.versionNumber}`;
        }

        // Add preview parameter
        queryParams = queryParams
            ? `${queryParams}&ep_preview=true`
            : `?ep_preview=true`;

        return url + queryParams;
    };

    // Fetch pages with cursor-based pagination handling
    const fetchAllPages = async (templateParam, searchQuery = '', keywordOnly = false) => {
        let allPages = [];
        let cursor = null;
        let prevCursor = null;
        let batchNumber = 0;
        const MAX_BATCHES = 20; // Safety limit

        // Start progress at 10%
        setFetchProgress(10);
        setTotalFetched(0);
        setPages([]);
        setIsLoadingMorePages(true);

        do {
            batchNumber++;

            // Set up API parameters
            const params = {
                limit: 1000, // Maximum allowed by Cloudflare KV
            };

            // Only include cursor if it exists
            if (cursor) {
                params.cursor = cursor;
            }

            // Add search query if exists
            if (searchQuery && searchQuery.trim() !== '') {
                params.search = searchQuery;
            }

            // Add keyword only flag if needed
            if (keywordOnly) {
                params.keyword_only = true;
            }

            try {
                // Call the appropriate API based on whether we have a templateId
                let data;
                if (templateParam) {
                    data = await api.pages.getAll(templateParam, params);
                } else {
                    data = await api.pages.getAll(params);
                }

                // Add pages from this batch to our collection
                allPages = [...allPages, ...data.items];
                setTotalFetched(allPages.length);

                // Update progress - calculate based on total if we have it
                const progressIncrement = data.total
                    ? (data.items.length / data.total) * 80 // 80% of progress bar reserved for loading
                    : (100 - 20) / MAX_BATCHES; // If no total, divide evenly

                setFetchProgress(prev => Math.min(90, prev + progressIncrement));

                // Store current cursor for comparison in next iteration
                prevCursor = cursor;
                cursor = data.next_cursor;

            } catch (error) {
                console.error('Error fetching pages:', error);
                toast.error("Error fetching pages", {
                    description: error.message
                });
                break;
            }
        } while (cursor);

        // Final state updates
        if (allPages.length > 0) {
            try {
                // Update progress to indicate we're fetching D1 data now
                setFetchProgress(92);

                // Initialize an empty version map to store analytics data
                const initialVersionMap = {};

                // Process the pages to setup the initial version map
                allPages.forEach(page => {
                    if (page.versions && page.versions.length > 0) {
                        // Update the pageVersions state with initially empty data
                        // We'll fetch the full analytics data below
                        initialVersionMap[page.id] = page.versions.map(version => ({
                            id: version.id,
                            versionNumber: version.versionNumber,
                            cache_key: version.id, // The ID is also the cache key
                            status: 'loading', // Will be updated with actual status later
                            total_views: 0,
                            last_viewed_on: null,
                            created_on: null
                        }));
                    } else {
                        // Handle legacy pages with no versions
                        initialVersionMap[page.id] = [];
                    }
                });

                // Start with just the KV data for all pages
                setPages(allPages);
                setFilteredPages(allPages);

                // Complete the progress bar to show main content
                setFetchProgress(100);
                setIsLoadingMorePages(false);

                // Initialize the pageVersions state with the initial version map
                setPageVersions(initialVersionMap);

                // Then progressively fetch metadata for each page and update the state
                // This happens after the initial page load
                await fetchMetadataForAllVersions(initialVersionMap);
            } catch (error) {
                console.error('Error fetching page data:', error);
                toast.error("Error fetching data", {
                    description: "Error loading pages data."
                });
                setPages([]);
                setFilteredPages([]);
                setFetchProgress(100);
                setIsLoadingMorePages(false);
            }
        } else {
            console.log('No pages found');
            setIsLoadingMorePages(false);
        }
    };

    // New function to fetch metadata for all versions after initial load
    const fetchMetadataForAllVersions = async (initialVersionMap) => {
        try {
            // Show a loading indicator
            toast.loading("Loading version analytics data...");
            setIsLoadingMetadata(true);

            // Extract all version cache keys from the version map
            const allVersionKeys = [];
            Object.values(initialVersionMap).forEach(versions => {
                versions.forEach(version => {
                    allVersionKeys.push(version.cache_key);
                });
            });

            if (allVersionKeys.length === 0) {
                toast.dismiss();
                toast.info("No versions found to load analytics for");
                setIsLoadingMetadata(false);
                return;
            }

            // Define batch size for API requests
            const BATCH_SIZE = 100; // Maximum allowed by the API

            // Create a copy of the page versions to update
            let updatedVersions = { ...initialVersionMap };
            let processedCount = 0;

            // Process versions in batches
            for (let i = 0; i < allVersionKeys.length; i += BATCH_SIZE) {
                const batchKeys = allVersionKeys.slice(i, i + BATCH_SIZE);

                // Skip if the batch is empty
                if (batchKeys.length === 0) continue;

                // Make API requests for analytics and status
                const [analyticsData, statusResponse] = await Promise.all([
                    // Fetch analytics data from D1
                    api.pages.getBatchMetadata(batchKeys.join(',')),

                    // Fetch version status from KV metadata
                    api.pages.getVersionStatus(batchKeys.join(','))
                ]);



                // Process the responses more robustly
                const analyticsResults = analyticsData?.results || {};
                const statusResults = statusResponse?.results || {};

                // Create a set of cache keys in this batch for efficient lookup
                const batchKeySet = new Set(batchKeys);

                // Only process pages that have versions in the current batch
                const pagesToProcess = Object.keys(updatedVersions).filter(pageId => {
                    return updatedVersions[pageId].some(version => batchKeySet.has(version.cache_key));
                });

                pagesToProcess.forEach(pageId => {
                    // Update each version with its analytics and status
                    updatedVersions[pageId] = updatedVersions[pageId].map(version => {
                        const cacheKey = version.cache_key;
                        const analytics = analyticsResults[cacheKey] || {};
                        const statusInfo = statusResults[cacheKey] || {};



                        const updatedVersion = {
                            ...version,
                            // Analytics data from D1
                            total_views: analytics.total_views || 0,
                            last_viewed_on: analytics.last_viewed_at || null,
                            created_on: analytics.created_at || null,
                            // Status data from KV metadata
                            status: statusInfo.status || 'unknown',
                            status_updated_at: statusInfo.updated_at || null
                        };



                        return updatedVersion;
                    });
                });

                // Update progress
                processedCount += batchKeys.length;

                // Update state after each batch to show progress
                setPageVersions(prevVersions => ({
                    ...prevVersions,
                    ...updatedVersions
                }));

                // Add small delay between batches to prevent overwhelming the server
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Complete
            toast.dismiss();
            toast.success(`Loaded analytics for ${processedCount} versions`);



            // Update the pageVersions state incrementally, merging with existing data
            setPageVersions(prevVersions => ({
                ...prevVersions,
                ...updatedVersions
            }));

            setIsLoadingMetadata(false);

        } catch (error) {
            console.error('Error fetching metadata for versions:', error);
            toast.dismiss();
            toast.error("Some version details could not be loaded", {
                description: "Basic version data is available but some metrics may be missing."
            });
            setIsLoadingMetadata(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch organization first
                const orgData = await api.organizations.getByPublicId(orgPublicId);
                if (orgData) {
                    setOrganization(orgData);

                    // Fetch template data
                    try {
                        const templateData = await api.templates.getByPublicId(templatePublicId);
                        if (templateData) {
                            setTemplate(templateData);

                            // Fetch pages for this template using the new batched approach
                            try {
                                await fetchAllPages(templateData.id, searchQuery, false);
                                // Note: The pages state will be updated by fetchAllPages
                            } catch (err) {
                                console.error("Failed to fetch pages:", err);
                                setError(err.message);
                                setPages([]);
                                setFilteredPages([]);
                            }
                        } else {
                            // Template not found
                            setError("Template not found");
                            setTimeout(() => {
                                navigate(`/organizations/${orgPublicId}/edit`);
                            }, 2000);
                        }
                    } catch (err) {
                        console.error("Failed to fetch template:", err);
                        setError(err.message);
                    }
                } else {
                    // Organization not found
                    setError("Organization not found");
                    setTimeout(() => {
                        navigate(`/organizations`);
                    }, 2000);
                }
            } catch (err) {
                console.error("Failed to fetch organization:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [orgPublicId, templatePublicId, navigate, api.organizations, api.templates, api.pages]);

    useEffect(() => {
        // Filter pages based on search query
        if (searchQuery.trim() === '') {
            setFilteredPages(pages);
        } else {
            const filtered = pages.filter(page =>
                page.keyword_phrase?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredPages(filtered);
        }
    }, [searchQuery, pages]);

    const handleSearch = (value) => {
        setSearchQuery(value);
    };

    const handleDeletePage = (page) => {
        setPageToDelete(page);
        setShowDeleteDialog(true);
    };

    const confirmDeletePage = async () => {
        if (!pageToDelete) return;

        setIsDeleting(true);
        try {
            // Delete the page in local state only since DELETE endpoint is removed

            // Update local state to remove the page
            const updatedPages = pages.filter(p => p.id !== pageToDelete.id);
            setPages(updatedPages);
            setFilteredPages(filteredPages.filter(p => p.id !== pageToDelete.id));

            // Show success message
            toast.success("Page deleted", {
                description: "The page has been removed from the local state."
            });

            // Close dialog and reset state
            setShowDeleteDialog(false);
            setPageToDelete(null);
        } catch (err) {
            console.error("Failed to delete page:", err);
            setError(err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleRegeneratePage = async (page) => {
        setIsRegenerating(true);
        try {
            // Update page state locally without API call since PUT endpoint is removed
            // In a real app, this would call an API to regenerate the page content

            // Update the page in local state only
            const updatedPages = pages.map(p => {
                if (p.id === page.id) {
                    return {
                        ...p,
                        last_updated: new Date().toISOString(),
                        needs_update: true
                    };
                }
                return p;
            });

            setPages(updatedPages);

            toast.success("Page marked for regeneration", {
                description: "The page has been marked for regeneration in the local state."
            });
        } catch (err) {
            console.error("Failed to regenerate page:", err);
            setError(err.message);
        } finally {
            setIsRegenerating(false);
        }
    };

    const handleOpenAddPagesDialog = () => {
        setKeywordsInput('');
        setShouldReprocess(false);
        setShowAddPagesDialog(true);
    };

    const handleKeywordsInputChange = (e) => {
        setKeywordsInput(e.target.value);
    };

    const handleAddPagesSubmit = async () => {
        if (!keywordsInput.trim()) {
            toast.error("No keywords provided", {
                description: "Please enter at least one keyword phrase."
            });
            return;
        }

        // Parse the keywords from comma-separated list
        const keywords = keywordsInput
            .split(',')
            .map(keyword => keyword.trim())
            .filter(keyword => keyword.length > 0);

        if (keywords.length === 0) {
            toast.error("No valid keywords found", {
                description: "Please enter at least one valid keyword phrase."
            });
            return;
        }

        if (keywords.length > 1000) {
            toast.error("Too many keywords", {
                description: "You can only generate pages for up to 1000 keywords at once."
            });
            return;
        }

        setIsAddingPages(true);

        try {
            // Per database requirements:
            // - custom_domain always ends with a slash
            // - template_path never starts with a slash but always ends with a slash
            const url = `${organization.custom_domain}${template.template_path}`;

            // Use the service binding API endpoint
            const response = await api.serviceBindings.generatePages({
                url,
                keywords,
                shouldReprocess: shouldReprocess
            });

            if (!response.success) {
                throw new Error(response.error || 'Failed to create pages');
            }

            // Store the job ID for future reference
            setJobId(response.jobId);

            // Show success message with Sonner
            toast.success("Pages creation started", {
                description: `Job created with ${keywords.length} keyword(s). Job ID: ${response.jobId}`
            });

            // Close the dialog
            setShowAddPagesDialog(false);

            // Refresh the pages list after a short delay (or could implement polling)
            setTimeout(async () => {
                try {
                    // Use the new batched approach when refreshing
                    await fetchAllPages(template.id, searchQuery, false);
                } catch (err) {
                    console.error("Failed to refresh pages:", err);
                }
            }, 5000);

        } catch (err) {
            console.error("Failed to create pages:", err);
            toast.error("Error creating pages", {
                description: err.message
            });
        } finally {
            setIsAddingPages(false);
        }
    };

    const checkJobStatus = async (jobId) => {
        try {
            // Use the service binding API endpoint
            const data = await api.serviceBindings.checkJobStatus(jobId);

            if (!data.success) {
                throw new Error(data.error || 'Failed to check job status');
            }

            return data;
        } catch (err) {
            console.error("Failed to check job status:", err);
            throw err;
        }
    };

    // Function to refresh pages data
    const handleRefreshPages = async () => {
        try {
            setLoading(true);
            await fetchAllPages(template.id, searchQuery, false);
            toast.success("Pages refreshed successfully");
        } catch (err) {
            console.error("Failed to refresh pages:", err);
            toast.error("Error refreshing pages", {
                description: err.message
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle row selection changes
    const handleSelectedRowsChange = (selected) => {
        setSelectedRows(selected);
    };

    // Bulk actions
    const handleBulkDelete = async () => {
        setIsBulkActionProcessing(true);
        try {
            // Get selected keyword phrases from the selected rows
            const keywordPhrases = selectedRows.map(page => page.keyword_phrase);

            // Construct the URL for the template
            const url = `${organization.custom_domain}${template.template_path}`;

            // Call the service binding to delete keyword phrases
            const response = await api.serviceBindings.deleteKeywordPhrases({
                url,
                keywordPhrases
            });

            if (!response.success) {
                throw new Error(response.error || 'Failed to delete keyword phrases');
            }

            // Check if this is a single deletion or a batch job
            if (response.jobId) {
                // Batch deletion - store job ID and start polling
                setDeletionJobId(response.jobId);
                setDeletionJobStatus({
                    status: 'queued',
                    totalKeywords: keywordPhrases.length,
                    deletedKeywords: 0,
                    progress: '0%'
                });

                // Start polling for job status
                startDeletionJobPolling(response.jobId);

                toast.success("Deletion job started", {
                    description: `Job created with ${keywordPhrases.length} keyword phrase(s). Job ID: ${response.jobId}`
                });

                // Clear the selected rows immediately after starting the job
                setSelectedRows([]);
            } else {
                // Single deletion - immediate response
                toast.success("Keyword phrase deleted", {
                    description: response.message || "The keyword phrase was deleted successfully."
                });

                // Update local state to remove the deleted pages
                const updatedPages = pages.filter(page => !selectedRows.some(selected => selected.id === page.id));
                setPages(updatedPages);
                setFilteredPages(filteredPages.filter(page => !selectedRows.some(selected => selected.id === page.id)));

                // Close dialog and reset selected rows
                setSelectedRows([]);
                setShowBulkActionDialog(false);
                setIsBulkActionProcessing(false);
            }
        } catch (err) {
            console.error("Failed to delete keyword phrases:", err);
            toast.error("Error deleting keyword phrases", {
                description: err.message
            });
            setIsBulkActionProcessing(false);
            setShowBulkActionDialog(false);
        }
    };

    // Function to start polling for job status
    const startDeletionJobPolling = async (jobId) => {
        setIsDeletionJobPolling(true);

        // Set up polling interval
        const pollInterval = setInterval(async () => {
            try {
                // Call the job status API
                const status = await api.serviceBindings.checkJobStatus(jobId);

                // Update status in state
                setDeletionJobStatus(status);

                // If job is completed or failed, stop polling
                if (status.status === 'completed' || status.status === 'failed') {
                    clearInterval(pollInterval);
                    setIsDeletionJobPolling(false);

                    // Show appropriate toast
                    if (status.status === 'completed') {
                        toast.success("Deletion job completed", {
                            description: `Successfully deleted ${status.deletedKeywords} keyword phrase(s).`
                        });

                        // Refresh the pages list
                        setTimeout(async () => {
                            try {
                                await fetchAllPages(template.id, searchQuery, false);
                            } catch (err) {
                                console.error("Failed to refresh pages:", err);
                            }
                        }, 2000);
                    } else {
                        toast.error("Deletion job failed", {
                            description: status.error || "Failed to complete the deletion job."
                        });
                    }

                    // Reset state after completion
                    setShowBulkActionDialog(false);
                    setIsBulkActionProcessing(false);
                    setTimeout(() => {
                        setDeletionJobId(null);
                        setDeletionJobStatus(null);
                    }, 5000);
                }
            } catch (err) {
                console.error("Error checking job status:", err);
                clearInterval(pollInterval);
                setIsDeletionJobPolling(false);
                toast.error("Error checking job status", {
                    description: err.message
                });

                // Reset state
                setShowBulkActionDialog(false);
                setIsBulkActionProcessing(false);
            }
        }, 3000); // Poll every 3 seconds

        // Cleanup function to clear interval if component unmounts
        return () => clearInterval(pollInterval);
    };

    const handleBulkRegenerate = async () => {
        setIsBulkActionProcessing(true);
        try {
            // Get selected keyword phrases from the selected rows
            const keywordPhrases = selectedRows.map(page => page.keyword_phrase);

            // Construct the URL for the template
            const url = `${organization.custom_domain}${template.template_path}`;

            // Call the service binding to regenerate pages
            const response = await api.serviceBindings.generatePages({
                url,
                keywords: keywordPhrases,
                shouldReprocess: true // Always set to true for regeneration
            });

            if (!response.success) {
                throw new Error(response.error || 'Failed to regenerate pages');
            }

            // Store the job ID for future reference
            setRegenerationJobId(response.jobId);
            setRegenerationJobStatus({
                status: 'queued',
                totalKeywords: selectedRows.length, // Use selectedRows.length directly
                processedKeywords: 0,
                progress: '0%'
            });

            // Start polling for job status
            startRegenerationJobPolling(response.jobId);

            // Show success message with Sonner
            toast.success("Regeneration started", {
                description: `Job created with ${keywordPhrases.length} keyword(s). Job ID: ${response.jobId}`
            });

            // Clear the selected rows immediately after starting the job
            setSelectedRows([]);
        } catch (err) {
            console.error("Failed to regenerate pages:", err);
            toast.error("Error regenerating pages", {
                description: err.message
            });
            setIsBulkActionProcessing(false);
            setShowBulkActionDialog(false);
        }
    };

    // Function to start polling for regeneration job status
    const startRegenerationJobPolling = async (jobId) => {
        setIsRegenerationJobPolling(true);

        // Set up polling interval
        const pollInterval = setInterval(async () => {
            try {
                // Call the job status API
                const status = await api.serviceBindings.checkJobStatus(jobId);

                // Update status in state with processed count
                setRegenerationJobStatus(prevStatus => ({
                    ...prevStatus,
                    ...status,
                    // Use completedKeywords from the API response
                    processedKeywords: status.completedKeywords || 0
                }));

                // If job is completed or failed, stop polling
                if (status.status === 'completed' || status.status === 'failed') {
                    clearInterval(pollInterval);
                    setIsRegenerationJobPolling(false);

                    // Show appropriate toast
                    if (status.status === 'completed') {
                        toast.success("Regeneration job completed", {
                            description: `Successfully regenerated ${status.completedKeywords || 0} page(s).`
                        });

                        // Refresh the pages list
                        setTimeout(async () => {
                            try {
                                await fetchAllPages(template.id, searchQuery, false);
                            } catch (err) {
                                console.error("Failed to refresh pages:", err);
                            }
                        }, 2000);
                    } else {
                        toast.error("Regeneration job failed", {
                            description: status.error || "Failed to complete the regeneration job."
                        });
                    }

                    // Reset state after completion
                    setShowBulkActionDialog(false);
                    setIsBulkActionProcessing(false);
                    setTimeout(() => {
                        setRegenerationJobId(null);
                        setRegenerationJobStatus(null);
                    }, 5000);
                }
            } catch (err) {
                console.error("Error checking regeneration job status:", err);
                clearInterval(pollInterval);
                setIsRegenerationJobPolling(false);
                toast.error("Error checking job status", {
                    description: err.message
                });

                // Reset state
                setShowBulkActionDialog(false);
                setIsBulkActionProcessing(false);
            }
        }, 3000); // Poll every 3 seconds

        // Cleanup function to clear interval if component unmounts
        return () => clearInterval(pollInterval);
    };

    const openBulkActionDialog = (action) => {
        setBulkAction(action);

        // Reset job status if opening a new action dialog
        if (action === 'regenerate') {
            setRegenerationJobStatus(null);
        } else if (action === 'delete') {
            setDeletionJobStatus(null);
        }

        setShowBulkActionDialog(true);
    };

    // Handle CSV download
    const handleDownloadCSV = async () => {
        // If there's no data to export, don't proceed
        if (filteredPages.length === 0) {
            toast.error("No data to export", {
                description: "There are no pages to export to CSV."
            });
            return;
        }

        try {
            // Show toast to indicate start of CSV generation
            toast.info("Preparing CSV export", {
                description: "This may take a moment for large datasets."
            });

            // Show progress dialog
            setIsGeneratingCSV(true);
            setCsvProgress(0);

            // First, create helper functions outside the column definitions
            // These will be easier to stringify and parse in the worker
            const getUrl = function (row) {
                return constructUnderscoreUrl(template, row);
            };

            const getLastViewedDate = function (row) {
                const versions = pageVersions[row.id] || [];
                let mostRecentViewDate = null;

                versions.forEach(version => {
                    if (version.last_viewed_on) {
                        const viewDate = new Date(version.last_viewed_on);
                        if (!mostRecentViewDate || viewDate > mostRecentViewDate) {
                            mostRecentViewDate = viewDate;
                        }
                    }
                });

                return mostRecentViewDate
                    ? formatDateToLocalTimezone(mostRecentViewDate)
                    : "Never";
            };

            const getCreatedDate = function (row) {
                const versions = pageVersions[row.id] || [];
                let oldestCreationDate = null;

                versions.forEach(version => {
                    if (version.created_on) {
                        const creationDate = new Date(version.created_on);
                        if (!oldestCreationDate || creationDate < oldestCreationDate) {
                            oldestCreationDate = creationDate;
                        }
                    }
                });

                return oldestCreationDate
                    ? formatDateToLocalTimezone(oldestCreationDate)
                    : "Unknown";
            };

            const getTotalViews = function (row) {
                const versions = pageVersions[row.id] || [];
                return versions.reduce((sum, version) => sum + (version.total_views || 0), 0);
            };

            // Define simple column configuration for CSV export
            // Use simpler function definitions for better serialization
            const csvColumns = [
                { header: "Keyword Phrase", accessorKey: "keyword_phrase" },
                { header: "URL", accessorFn: "return constructPageUrl(template, row)" },
                { header: "Total Views", accessorKey: "total_views" },
                { header: "Last Viewed On", accessorFn: "return row.last_viewed_on ? formatDateToLocalTimezone(row.last_viewed_on) : 'Never'" },
                { header: "Created On", accessorFn: "return row.created_on ? formatDateToLocalTimezone(row.created_on) : 'Unknown'" }
            ];

            // Determine which data set to use (filtered or all)
            const dataToExport = filteredPages.map(page => ({
                ...page,
                // Pre-compute complex fields to avoid serialization issues
                csv_url: getUrl(page),
                csv_last_viewed: getLastViewedDate(page),
                csv_created: getCreatedDate(page),
                csv_total_views: getTotalViews(page)
            }));

            // Use simpler columns that use pre-computed values
            const simplifiedColumns = [
                { header: "Keyword Phrase", accessorKey: "keyword_phrase" },
                { header: "URL", accessorKey: "csv_url" },
                { header: "Total Views", accessorKey: "csv_total_views" },
                { header: "Last Viewed On", accessorKey: "csv_last_viewed" },
                { header: "Created On", accessorKey: "csv_created" }
            ];

            const timestamp = new Date().toISOString().split('T')[0];
            const fileName = `${template.name.replace(/\s+/g, '-')}-pages-${timestamp}.csv`;

            // Create the CSV export handler and register progress callback
            const csvExportHandler = exportTableToCSV(dataToExport, simplifiedColumns, fileName);

            // Register progress callback
            csvExportHandler.onProgress((progress) => {
                setCsvProgress(progress);
            });

            // Start the CSV generation process
            const result = await csvExportHandler.start();

            // Once complete, close the dialog and trigger download
            if (result.status === 'complete') {
                setIsGeneratingCSV(false);

                // Trigger the download
                result.download();

                // Show success toast
                toast.success("CSV file ready", {
                    description: `${result.rowCount} rows exported to ${fileName}`
                });
            }
        } catch (error) {
            console.error("Failed to generate CSV:", error);
            toast.error("Failed to generate CSV", {
                description: error.message
            });
            setIsGeneratingCSV(false);
        }
    };

    // Calculate page count when filteredPages or sortConfig changes
    useEffect(() => {
        const sortedData = getSortedData(filteredPages);
        setPageCount(Math.ceil(sortedData.length / pageSize));
    }, [filteredPages, pageSize, sortConfig]);

    // Function to handle version deletion from the preview sheet
    const handleDeleteVersionInPreview = async (version, page) => {
        try {
            // Show loading toast
            toast.loading(`Deleting version v${version.versionNumber}...`);

            // Get the current versions list before deletion
            const currentVersions = [...pageVersionsList];

            // Find the current index in versions list
            const currentIndex = currentVersions.findIndex(v => v.id === version.id);

            // Determine which version to show next after deletion
            let nextVersionIndex = null;
            if (currentVersions.length > 1) {
                // If this is the last version in the list, go to the previous one
                if (currentIndex === currentVersions.length - 1) {
                    nextVersionIndex = currentIndex - 1;
                } else {
                    // Otherwise go to the next one
                    nextVersionIndex = currentIndex;
                }
            }

            // Call the delete-page-version service binding
            const response = await api.serviceBindings.deletePageVersion({
                versionKey: version.id
            });

            if (response && response.success) {
                toast.dismiss();
                toast.success(`Version v${version.versionNumber} deleted successfully`);

                // Update local state to remove the deleted version
                setPageVersions(prev => {
                    const updated = { ...prev };

                    // Find the page and remove the version
                    if (updated[page.id]) {
                        updated[page.id] = updated[page.id].filter(v => v.id !== version.id);
                    }

                    return updated;
                });

                // Update the preview versions list
                const updatedVersionsList = currentVersions.filter(v => v.id !== version.id);
                setPageVersionsList(updatedVersionsList);

                // Handle navigation after deletion
                if (updatedVersionsList.length === 0) {
                    // If no versions left, close the sheet
                    setIsPreviewOpen(false);
                } else {
                    // Otherwise navigate to the next version
                    setCurrentVersionIndex(nextVersionIndex);
                    setPreviewVersion(updatedVersionsList[nextVersionIndex]);
                    setPreviewPrompt(''); // Reset the prompt for the new version
                }
            } else {
                toast.dismiss();
                toast.error("Failed to delete version", {
                    description: response?.error || "Unknown error occurred"
                });
            }
        } catch (error) {
            console.error('Error deleting version:', error);
            toast.dismiss();
            toast.error("Error deleting version", {
                description: error.message
            });
        }
    };

    // Add this function to handle version deletion
    const handleDeleteVersion = async (version, page) => {
        try {
            // Ask for confirmation
            if (!confirm(`Are you sure you want to delete version v${version.versionNumber} of "${page.keyword_phrase}"? This action cannot be undone.`)) {
                return;
            }

            // Show loading toast
            toast.loading(`Deleting version v${version.versionNumber}...`);

            // Call the delete-page-version service binding
            const response = await api.serviceBindings.deletePageVersion({
                versionKey: version.id
            });

            if (response && response.success) {
                toast.dismiss();
                toast.success(`Version v${version.versionNumber} deleted successfully`);

                // Update local state to remove the deleted version
                setPageVersions(prev => {
                    const updated = { ...prev };

                    // Find the page and remove the version
                    if (updated[page.id]) {
                        updated[page.id] = updated[page.id].filter(v => v.id !== version.id);
                    }

                    return updated;
                });

                // NOTE: We are skipping preview navigation logic here
                // because that's now handled by handleDeleteVersionInPreview
                // when deleting directly from the preview sheet
            } else {
                toast.dismiss();
                toast.error("Failed to delete version", {
                    description: response?.error || "Unknown error occurred"
                });
            }
        } catch (error) {
            console.error('Error deleting version:', error);
            toast.dismiss();
            toast.error("Error deleting version", {
                description: error.message
            });
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto p-4">
                <div className="flex flex-col justify-center items-center h-64">
                    <div data-testid="loading-spinner" className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 mb-4"></div>
                    <p className="text-gray-600">Loading data...</p>
                </div>
            </div>
        );
    }

    if (isLoadingMorePages) {
        return (
            <div className="container mx-auto p-4">
                <div className="flex flex-col justify-center items-center h-64">
                    <div className="w-full max-w-md mb-4">
                        <h3 className="text-lg font-medium mb-2">Loading Pages</h3>
                        <Progress value={fetchProgress} className="h-2 mb-2" />
                        <p className="text-sm text-gray-600">
                            {totalFetched} pages loaded so far...
                        </p>
                    </div>
                    <div data-testid="loading-spinner" className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 mb-4"></div>
                    <p className="text-gray-600 text-sm">This may take a while if there are many pages.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>
                        {error}
                    </AlertDescription>
                </Alert>
                <div className="mt-4">
                    <Button
                        variant="outline"
                        onClick={() => navigate(`/organizations/${organization.public_id}/edit`)}
                    >
                        Back to Organization
                    </Button>
                </div>
            </div>
        );
    }

    if (!template || !organization) {
        return (
            <div className="container mx-auto p-4">
                <div className="text-center">
                    <h2 className="text-xl">Loading data...</h2>
                </div>
            </div>
        );
    }

    // Define the columns for our DataTable
    const columns = getColumns(
        navigate,
        constructPageUrl,
        organization,
        template,
        organization.public_id,
        template.public_id
    );

    return (
        <div className="container mx-auto p-4">
            <Toaster position="top-right" />
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h1 className="text-2xl font-bold mb-1">{template.name} Pages</h1>
                    <p className="text-gray-500">Organization: {organization.name}</p>
                </div>
                <div className="flex space-x-2">
                    <Button
                        variant="outline"
                        onClick={() => navigate(`/organizations/${organization.public_id}/edit`)}
                    >
                        Back to Organization
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleRefreshPages}
                    >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Refresh
                    </Button>
                    <Button
                        onClick={handleOpenAddPagesDialog}
                    >
                        Add Pages
                    </Button>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Pages</CardTitle>
                    <CardDescription>
                        Manage pages for this template
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {/* Create the simplified columns here with access to the component context */}
                    <CollapsibleDataTable
                        data={filteredPages}
                        columns={[
                            {
                                id: 'select',
                                header: ({ table }) => {
                                    // Calculate current page data directly in this scope
                                    const sortedData = getSortedData(filteredPages);
                                    const startIndex = currentPage * pageSize;
                                    const endIndex = Math.min(startIndex + pageSize, sortedData.length);
                                    const currentPageData = sortedData.slice(startIndex, endIndex);

                                    return (
                                        <div className="flex justify-center">
                                            <Checkbox
                                                className="h-4 w-4"
                                                checked={
                                                    currentPageData.length > 0 &&
                                                    currentPageData.every(row => selectedRows.some(selected => selected.id === row.id))
                                                }
                                                onCheckedChange={(checked) => {
                                                    if (checked) {
                                                        // Add only current page rows that aren't already selected
                                                        const currentPageIds = new Set(currentPageData.map(row => row.id));
                                                        const newSelectedRows = [
                                                            ...selectedRows.filter(row => !currentPageIds.has(row.id)), // Keep selected rows from other pages
                                                            ...currentPageData // Add all rows from current page
                                                        ];
                                                        handleSelectedRowsChange(newSelectedRows);
                                                    } else {
                                                        // Remove only current page rows from selection
                                                        const currentPageIds = new Set(currentPageData.map(row => row.id));
                                                        handleSelectedRowsChange(
                                                            selectedRows.filter(row => !currentPageIds.has(row.id))
                                                        );
                                                    }
                                                }}
                                                aria-label="Select all visible rows"
                                            />
                                        </div>
                                    );
                                },
                                cell: (page) => (
                                    <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            className="h-4 w-4"
                                            checked={selectedRows.some(row => row.id === page.id)}
                                            onCheckedChange={(checked) => {
                                                if (checked) {
                                                    handleSelectedRowsChange([...selectedRows, page]);
                                                } else {
                                                    handleSelectedRowsChange(selectedRows.filter(row => row.id !== page.id));
                                                }
                                            }}
                                            aria-label="Select row"
                                        />
                                    </div>
                                )
                            },
                            {
                                id: 'keyword_phrase',
                                header: 'Keyword Phrase',
                                accessorKey: 'keyword_phrase',
                                cell: (page) => (
                                    <div className="font-medium">{page.keyword_phrase}</div>
                                )
                            },
                            {
                                id: 'url',
                                header: 'URL',
                                cell: (page) => {
                                    const url = constructUnderscoreUrl(template, page);
                                    return (
                                        <div className="flex space-x-2 justify-center" onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-8 w-8 p-0"
                                                title="Copy URL"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(url);
                                                    toast.success("URL copied to clipboard");
                                                }}
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-8 w-8 p-0"
                                                title="Visit URL"
                                                onClick={() => window.open(url, '_blank')}
                                            >
                                                <ExternalLink className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    );
                                }
                            },
                            {
                                id: 'live_versions',
                                header: 'Live',
                                cell: (page) => {
                                    const versions = pageVersions[page.id] || [];
                                    let liveVersions = [];

                                    // Filter for versions with 'live' status
                                    if (versions.length > 0) {
                                        liveVersions = versions
                                            .filter(version => version.status === 'live')
                                            .map(version => `v${version.versionNumber}`);
                                    }

                                    // Show loading state if versions exist but status is still loading
                                    const isLoading = versions.some(v => v.status === 'loading');

                                    if (isLoading) {
                                        return (
                                            <div className="text-center">
                                                <span className="text-xs text-gray-500">Loading...</span>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div className="text-center">
                                            {liveVersions.length > 0 ? (
                                                <span className="px-2 py-1 text-xs bg-green-50 text-green-800 rounded-full">
                                                    {liveVersions.join(', ')}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">None</span>
                                            )}
                                        </div>
                                    );
                                }
                            },
                            {
                                id: 'total_views',
                                header: 'Total Views',
                                accessorKey: 'total_views',
                                cell: (page) => {
                                    const versions = pageVersions[page.id] || [];
                                    // Sum up total views from all versions
                                    const totalViews = versions.reduce((sum, version) => sum + (version.total_views || 0), 0);
                                    return (
                                        <div className="text-center">{totalViews}</div>
                                    );
                                }
                            },
                            {
                                id: 'last_viewed_on',
                                header: 'Last Viewed On',
                                accessorKey: 'last_viewed_on',
                                cell: (page) => {
                                    const versions = pageVersions[page.id] || [];
                                    // Find most recent view date across all versions
                                    let mostRecentViewDate = null;

                                    versions.forEach(version => {
                                        if (version.last_viewed_on) {
                                            const viewDate = new Date(version.last_viewed_on);
                                            if (!mostRecentViewDate || viewDate > mostRecentViewDate) {
                                                mostRecentViewDate = viewDate;
                                            }
                                        }
                                    });

                                    return (
                                        <div className="text-center">
                                            {mostRecentViewDate
                                                ? formatDateToLocalTimezone(mostRecentViewDate)
                                                : "Never"}
                                        </div>
                                    );
                                }
                            },
                            {
                                id: 'created_on',
                                header: 'Created On',
                                accessorKey: 'created_on',
                                cell: (page) => {
                                    const versions = pageVersions[page.id] || [];
                                    // Find oldest creation date across all versions
                                    let oldestCreationDate = null;

                                    versions.forEach(version => {
                                        if (version.created_on) {
                                            const creationDate = new Date(version.created_on);
                                            if (!oldestCreationDate || creationDate < oldestCreationDate) {
                                                oldestCreationDate = creationDate;
                                            }
                                        }
                                    });

                                    return (
                                        <div className="text-center">
                                            {oldestCreationDate
                                                ? formatDateToLocalTimezone(oldestCreationDate)
                                                : "Unknown"}
                                        </div>
                                    );
                                }
                            }
                        ]}
                        onSearch={handleSearch}
                        searchQuery={searchQuery}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={handleSelectedRowsChange}
                        onBulkAction={openBulkActionDialog}
                        isLoadingMetadata={isLoadingMetadata}
                        onExportCSV={isGeneratingCSV ? null : handleDownloadCSV}
                        expandedRows={expandedRows}
                        onRowClick={toggleRowExpansion}
                        pageVersions={pageVersions}
                        organization={organization}
                        template={template}
                        page={currentPage}
                        pageCount={pageCount}
                        onPageChange={setCurrentPage}
                        pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </CardContent>
            </Card>

            {/* Delete Confirmation Dialog */}
            {showDeleteDialog && (
                <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Confirm Deletion</DialogTitle>
                            <DialogDescription>
                                Are you sure you want to delete the page with keyword phrase "{pageToDelete?.keyword_phrase}"?
                                This action cannot be undone.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setShowDeleteDialog(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={confirmDeletePage}
                                disabled={isDeleting}
                            >
                                {isDeleting ? "Deleting..." : "Delete"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {/* Add Pages Dialog */}
            <Dialog open={showAddPagesDialog} onOpenChange={setShowAddPagesDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Pages</DialogTitle>
                        <DialogDescription>
                            Enter keyword phrases separated by commas. These will be used to generate personalized pages.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Textarea
                            placeholder="Enter keyword phrases (e.g. digital marketing, web development, seo optimization)"
                            value={keywordsInput}
                            onChange={handleKeywordsInputChange}
                            rows={15}
                            style={{ height: "300px", maxHeight: "300px", overflowY: "auto" }}
                            className="w-full"
                        />
                        <p className="text-sm text-gray-500 mt-2">
                            Each keyword phrase will create a separate page.
                        </p>

                        <div className="flex items-center space-x-2 mt-4">
                            <Switch
                                id="reprocess-toggle"
                                checked={shouldReprocess}
                                onCheckedChange={setShouldReprocess}
                            />
                            <Label htmlFor="reprocess-toggle">Reprocess existing pages</Label>
                        </div>
                        <p className="text-sm text-gray-500 mt-1 ml-6">
                            When enabled, existing pages with the same keywords will be regenerated.
                            Otherwise, existing pages will be skipped.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowAddPagesDialog(false)}
                            disabled={isAddingPages}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddPagesSubmit}
                            disabled={isAddingPages}
                        >
                            {isAddingPages ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating Pages...
                                </>
                            ) : "Add Pages"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Action Confirmation Dialog */}
            <Dialog open={showBulkActionDialog} onOpenChange={setShowBulkActionDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {bulkAction === 'delete' ? 'Confirm Keyword Phrase Deletion' : 'Confirm Bulk Regeneration'}
                        </DialogTitle>
                        <DialogDescription>
                            {bulkAction === 'delete'
                                ? `Are you sure you want to delete ${selectedRows.length} keyword phrase(s)? This action cannot be undone.`
                                : `Are you sure you want to regenerate ${selectedRows.length} page(s)?`
                            }
                        </DialogDescription>
                    </DialogHeader>

                    {/* Display job status if we have a deletion job in progress */}
                    {bulkAction === 'delete' && deletionJobStatus && (
                        <div className="py-4">
                            <h3 className="font-medium text-sm mb-2">Deletion Job Status</h3>
                            <div className="mb-2">
                                <div className="flex justify-between text-sm mb-1">
                                    <span>Status: <span className="font-medium">{deletionJobStatus.status}</span></span>
                                    <span>{deletionJobStatus.progress}</span>
                                </div>
                                <Progress
                                    value={parseInt(deletionJobStatus.progress) || 0}
                                    className="h-2"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>Total Keywords: <span className="font-medium">{deletionJobStatus.totalKeywords || 0}</span></div>
                                <div>Deleted: <span className="font-medium">{deletionJobStatus.deletedKeywords || 0}</span></div>
                            </div>
                            {deletionJobStatus.failedKeywords > 0 && (
                                <div className="mt-2 text-red-500 text-sm">
                                    Failed: <span className="font-medium">{deletionJobStatus.failedKeywords}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Display job status if we have a regeneration job in progress */}
                    {bulkAction === 'regenerate' && regenerationJobStatus && (
                        <div className="py-4">
                            <h3 className="font-medium text-sm mb-2">Regeneration Job Status</h3>
                            <div className="mb-2">
                                <div className="flex justify-between text-sm mb-1">
                                    <span>Status: <span className="font-medium">{regenerationJobStatus.status}</span></span>
                                    <span>{regenerationJobStatus.progress}</span>
                                </div>
                                <Progress
                                    value={parseInt(regenerationJobStatus.progress) || 0}
                                    className="h-2"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>Total Keywords: <span className="font-medium">{regenerationJobStatus.totalKeywords || 0}</span></div>
                                <div>Processed: <span className="font-medium">{regenerationJobStatus.processedKeywords || regenerationJobStatus.completedKeywords || 0}</span></div>
                            </div>
                            {regenerationJobStatus.failedKeywords > 0 && (
                                <div className="mt-2 text-red-500 text-sm">
                                    Failed: <span className="font-medium">{regenerationJobStatus.failedKeywords}</span>
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowBulkActionDialog(false)}
                            disabled={isBulkActionProcessing}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant={bulkAction === 'delete' ? "destructive" : "default"}
                            onClick={bulkAction === 'delete' ? handleBulkDelete : handleBulkRegenerate}
                            disabled={isBulkActionProcessing || isDeletionJobPolling || isRegenerationJobPolling}
                        >
                            {isBulkActionProcessing
                                ? (bulkAction === 'delete' ? "Deleting..." : "Regenerating...")
                                : (bulkAction === 'delete' ? "Delete Phrases" : "Regenerate")
                            }
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* CSV Download Progress Dialog */}
            <DownloadProgressDialog
                open={isGeneratingCSV}
                progress={csvProgress}
                onOpenChange={(open) => {
                    if (!open) setIsGeneratingCSV(false);
                }}
            />

            {/* Add Preview Sheet */}
            <Sheet open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
                <SheetContentFull className="p-0 overflow-hidden">
                    <div className="flex h-full">
                        {/* Page Preview (iframe) - maximize this space */}
                        <div className="flex-1 h-full">
                            {previewVersion && previewPage && (
                                <iframe
                                    src={constructPageUrl(template, previewPage, previewVersion)}
                                    className="w-full h-full border-0"
                                    title={`Preview of ${previewPage.keyword_phrase} v${previewVersion.versionNumber}`}
                                    sandbox="allow-same-origin allow-scripts"
                                ></iframe>
                            )}
                        </div>

                        {/* Edit Form - make this skinnier */}
                        <div className="w-80 h-full border-l bg-background p-6 flex flex-col">
                            <SheetHeader className="mb-4">
                                <SheetTitle>Edit Page</SheetTitle>
                                <SheetDescription>
                                    Provide instructions for changes
                                </SheetDescription>
                            </SheetHeader>

                            <div className="mb-4">
                                <p className="text-sm font-medium mb-1">Keyword Phrase</p>
                                <p className="text-base break-words">{previewPage?.keyword_phrase}</p>
                            </div>

                            <div className="mb-4">
                                <p className="text-sm font-medium mb-1">Status</p>
                                <div className="flex items-center space-x-2">
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span
                                                    className={`px-2 py-1 rounded-full text-xs font-medium ${previewVersion?.status === 'unknown'
                                                        ? "bg-gray-100 text-gray-800"
                                                        : previewVersion?.status === "live"
                                                            ? "bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer"
                                                            : "bg-gray-100 text-gray-800 hover:bg-gray-200 cursor-pointer"
                                                        }`}
                                                    style={{
                                                        cursor: previewVersion?.status !== 'unknown' ? 'pointer' : 'default'
                                                    }}
                                                    onClick={(e) => {
                                                        if (previewVersion?.status === 'unknown') return;
                                                        e.stopPropagation();
                                                        const newStatus = previewVersion?.status === 'live' ? 'archived' : 'live';
                                                        updateVersionStatus(previewVersion?.cache_key, newStatus);
                                                    }}
                                                >
                                                    {previewVersion?.status === 'unknown' ? 'Unknown' : previewVersion?.status}
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {previewVersion?.status === 'unknown'
                                                    ? 'Unknown page status'
                                                    : previewVersion?.status === 'live'
                                                        ? 'Click to archive'
                                                        : 'Click to make live'}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>

                                    {previewVersion?.status === 'archived' && (
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => {
                                                            if (previewPage && previewVersion) {
                                                                // Ask for confirmation
                                                                if (confirm(`Are you sure you want to delete version v${previewVersion.versionNumber} of "${previewPage.keyword_phrase}"? This action cannot be undone.`)) {
                                                                    // Instead of just closing the sheet, let's delete and handle navigation
                                                                    handleDeleteVersionInPreview(previewVersion, previewPage);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Trash className="h-3 w-3" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>Delete version</TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )}
                                </div>
                            </div>

                            <div className="mb-4">
                                <p className="text-sm font-medium mb-1">Version</p>
                                <div className="flex justify-between items-center">
                                    <p className="text-base">v{previewVersion?.versionNumber}</p>
                                    {pageVersionsList.length > 1 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handlePreviewNextVersion}
                                            className="flex items-center h-8 py-0 px-2 text-xs"
                                        >
                                            Preview Next <ArrowRight className="ml-1 h-3 w-3" />
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div className="mb-4 flex-1">
                                <Label htmlFor="edit-prompt" className="mb-2 block">
                                    Edit Instructions
                                </Label>
                                <Textarea
                                    id="edit-prompt"
                                    placeholder="Describe how this page should be modified..."
                                    className="resize-none min-h-[20vh]"
                                    value={previewPrompt}
                                    onChange={(e) => setPreviewPrompt(e.target.value)}
                                />
                            </div>

                            <SheetFooter className="mt-auto">
                                <Button
                                    onClick={handleSubmitPreviewEdit}
                                    disabled={!previewPrompt.trim() || isSubmittingEdit}
                                >
                                    {isSubmittingEdit ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Submitting...
                                        </>
                                    ) : "Submit Edit"}
                                </Button>
                            </SheetFooter>
                        </div>
                    </div>
                </SheetContentFull>
            </Sheet>
        </div>
    );
};

export default PagesIndex; 
